class ProductStickySidebar {
  constructor() {
    this.stickyElement = document.querySelector('.sticky-desktop');
    this.scrollerElement = document.querySelector('.container-scroller');
    this.header = this.findHeader();
    
    if (!this.stickyElement || !this.scrollerElement) {
      return;
    }

    this.isDesktop = window.matchMedia('(min-width: 768px)').matches;
    this.headerHeight = this.getHeaderHeight();
    this.originalPosition = null;
    this.currentState = 'original'; 
    this.throttleTimer = null;
    
    this.init();
  }

  findHeader() {
    const selectors = [
      'header',
      '.header',
      '[data-header]',
      'sticky-header',
      '.site-header',
      '#header'
    ];
    
    for (let selector of selectors) {
      const element = document.querySelector(selector);
      if (element) return element;
    }
    
    return null;
  }

  init() {
    if (!this.isDesktop) {
      return;
    }

    if (document.readyState === 'loading') {
      window.addEventListener('load', () => {
        this.setup();
      });
    } else {
      this.setup();
    }
  }

  setup() {
    this.saveOriginalPosition();
    
    window.addEventListener('scroll', this.throttledScroll.bind(this));
    window.addEventListener('resize', this.handleResize.bind(this));
    
    document.addEventListener('shopify:section:load', this.handleSectionReload.bind(this));
    document.addEventListener('shopify:section:unload', this.cleanup.bind(this));
  }

  throttledScroll() {
    if (this.throttleTimer) return;
    
    this.throttleTimer = requestAnimationFrame(() => {
      this.handleScroll();
      this.throttleTimer = null;
    });
  }

  getHeaderHeight() {
    if (!this.header) return 80; 
    
    const rect = this.header.getBoundingClientRect();
    return rect.height || 80;
  }

  saveOriginalPosition() {
    const rect = this.stickyElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    const parentElement = this.stickyElement.offsetParent || document.body;
    const parentRect = parentElement.getBoundingClientRect();
    const parentScrollTop = parentElement.scrollTop || 0;
    
    this.originalPosition = {
      top: rect.top + scrollTop,
      left: rect.left,
      width: rect.width,
      parent: this.stickyElement.parentElement,
      relativeTop: rect.top + scrollTop - (parentRect.top + scrollTop) + parentScrollTop,
      relativeLeft: rect.left - parentRect.left
    };
  }

  handleScroll() {
    if (!this.isDesktop || !this.originalPosition) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const stickyTop = this.originalPosition.top;
    const triggerPoint = stickyTop - this.headerHeight - 20; 
    
    const scrollerRect = this.scrollerElement.getBoundingClientRect();
    const scrollerScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollerTop = scrollerRect.top + scrollerScrollTop;
    const scrollerBottom = scrollerTop + this.scrollerElement.offsetHeight;
    
    const stickyElementHeight = this.stickyElement.offsetHeight;
    
    const attachmentPoint = scrollerBottom - stickyElementHeight - this.headerHeight - 20;
    
    if (scrollTop < triggerPoint) {
      this.makeOriginal();
    } else if (scrollTop >= triggerPoint && scrollTop < attachmentPoint) {
      this.makeSticky();
    } else {
      this.makeBottomAttached();
    }
  }

  makeSticky() {
    if (this.currentState === 'sticky') return;

    const previousState = this.currentState;
    this.currentState = 'sticky';
    
    if (previousState === 'bottom-attached') {
      const currentRect = this.stickyElement.getBoundingClientRect();
      const currentAbsoluteLeft = currentRect.left;
      
      const parentElement = this.stickyElement.parentElement;
      if (parentElement && parentElement.style.position === 'relative') {
        parentElement.style.position = '';
      }
      
      Object.assign(this.stickyElement.style, {
        position: 'fixed',
        top: `${currentRect.top}px`, 
        left: `${currentAbsoluteLeft}px`,
        right: 'auto',
        width: `${this.originalPosition.width}px`,
        zIndex: '100',
        transition: 'none'
      });
      
      this.stickyElement.offsetHeight;
      
      setTimeout(() => {
        this.stickyElement.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        this.stickyElement.style.top = `${this.headerHeight + 20}px`;
        this.stickyElement.style.left = `${this.originalPosition.left}px`;
      }, 10);
    } else {
      const parentElement = this.stickyElement.parentElement;
      if (parentElement && parentElement.style.position === 'relative') {
        parentElement.style.position = '';
      }
      
      Object.assign(this.stickyElement.style, {
        position: 'fixed',
        top: `${this.headerHeight + 20}px`,
        left: `${this.originalPosition.left}px`,
        right: 'auto',
        width: `${this.originalPosition.width}px`,
        zIndex: '100',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      });
    }

    this.stickyElement.classList.remove('is-bottom-attached', 'is-original');
    this.stickyElement.classList.add('is-sticky');
    
    this.dispatchEvent('sticky:activated');
  }

  makeBottomAttached() {
    if (this.currentState === 'bottom-attached') return;

    this.currentState = 'bottom-attached';
    
    const currentRect = this.stickyElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const currentAbsoluteTop = currentRect.top + scrollTop;
    const currentAbsoluteLeft = currentRect.left;
    
    const parentElement = this.stickyElement.parentElement;
    if (parentElement && getComputedStyle(parentElement).position === 'static') {
      parentElement.style.position = 'relative';
    }
    
    const parentRect = parentElement.getBoundingClientRect();
    const parentAbsoluteTop = parentRect.top + scrollTop;
    
    const topRelativeToParent = currentAbsoluteTop - parentAbsoluteTop;
    const leftRelativeToParent = currentAbsoluteLeft - parentRect.left;
    
    Object.assign(this.stickyElement.style, {
      position: 'absolute',
      top: `${topRelativeToParent}px`,
      left: `${leftRelativeToParent}px`,
      right: 'auto',
      width: `${this.originalPosition.width}px`,
      zIndex: '100',
      transition: 'none' 
    });

    this.stickyElement.offsetHeight;
    
    const scrollerRect = this.scrollerElement.getBoundingClientRect();
    const scrollerAbsoluteBottom = scrollerRect.bottom + scrollTop;
    const stickyHeight = this.stickyElement.offsetHeight;
    const finalTopRelativeToParent = scrollerAbsoluteBottom - parentAbsoluteTop - stickyHeight;
    
    setTimeout(() => {
      this.stickyElement.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      this.stickyElement.style.top = `${finalTopRelativeToParent}px`;
    }, 10);

    this.stickyElement.classList.remove('is-sticky', 'is-original');
    this.stickyElement.classList.add('is-bottom-attached');
    
    this.dispatchEvent('sticky:bottom-attached');
  }

  makeOriginal() {
    if (this.currentState === 'original') return;

    this.currentState = 'original';

    const parentElement = this.stickyElement.parentElement;
    if (parentElement && parentElement.style.position === 'relative') {
      parentElement.style.position = '';
    }

    Object.assign(this.stickyElement.style, {
      position: '',
      top: '',
      left: '',
      right: '',
      width: '',
      zIndex: '',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    });

    this.stickyElement.classList.remove('is-sticky', 'is-bottom-attached');
    this.stickyElement.classList.add('is-original');
    
    this.dispatchEvent('sticky:original');
  }

  makeRelative() {
    this.makeOriginal();
  }

  handleResize() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.matchMedia('(min-width: 768px)').matches;
    
    if (wasDesktop !== this.isDesktop) {
      if (this.currentState !== 'original') {
        this.makeOriginal();
      }
      
      if (this.isDesktop) {
        requestAnimationFrame(() => {
          this.saveOriginalPosition();
          this.headerHeight = this.getHeaderHeight();
          this.handleScroll();
        });
      }
    } else if (this.isDesktop) {
      requestAnimationFrame(() => {
        this.saveOriginalPosition();
        this.headerHeight = this.getHeaderHeight();
        this.handleScroll(); 
      });
    }
  }

  handleSectionReload() {
    if (this.currentState !== 'original') {
      this.makeOriginal();
    }
    
    setTimeout(() => {
      this.stickyElement = document.querySelector('.sticky-desktop');
      this.scrollerElement = document.querySelector('.container-scroller');
      
      if (this.stickyElement && this.scrollerElement && this.isDesktop) {
        this.saveOriginalPosition();
        this.handleScroll();
      }
    }, 100);
  }

  cleanup() {
    if (this.currentState !== 'original') {
      this.makeOriginal();
    }
    
    if (this.throttleTimer) {
      cancelAnimationFrame(this.throttleTimer);
    }
  }

  dispatchEvent(eventName) {
    const event = new CustomEvent(eventName, {
      detail: {
        stickyElement: this.stickyElement,
        currentState: this.currentState
      }
    });
    document.dispatchEvent(event);
  }
}

document.addEventListener('DOMContentLoaded', function() {
  new ProductStickySidebar();
});

document.addEventListener('shopify:section:load', function() {
  new ProductStickySidebar();
});
